import { zodResolver } from "@hookform/resolvers/zod";
import { Button, IconButton } from "@hortis/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@hortis/ui/dialog";
import styles from "@hortis/ui/dialog/dialog.module.css";
import { FormControl, FormField, FormItem, FormMessage } from "@hortis/ui/form";
import { XClose } from "@hortis/ui/icons";
import { RichTextEditor } from "@hortis/ui/rich-text-editor";
import { toast } from "@hortis/ui/sonner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { useParams, useRouteContext } from "@tanstack/react-router";
import { ObservationConditionType, namedOperations } from "generated/graphql";
import { ReactNode, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { queryClient } from "src/app";
import { ObservationConditionPicker } from "src/components/pickers/observation-condition-picker/observation-condition-picker";
import { mention } from "src/components/rich-text-editor/mention";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import { DiscardDialog } from "src/features/create-accession/components/accession-data-form/discard-dialog";
import { useGqlMutation } from "src/utils/hooks/graphql";
import { observationConditionLabelMap } from "src/utils/label-maps/observation-condition";
import { tiptapSchema } from "src/utils/tiptap";
import { useAccessTokenBasic } from "src/utils/use-access-token";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { ObservationConditionRating } from "../sections/material-record-observations/observation-condition";
import { updateObservationDocument } from "./graphql";

export const updateObservationSchema = z.object({
  condition: z.nativeEnum(ObservationConditionType),
  bodyData: tiptapSchema.nullish(),
});

export const UpdateObservationDialog = ({
  open: openControlled,
  onOpenChange: onOpenChangeControlled,
  observationId,
  children,
  initialValues,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  initialValues: Partial<z.infer<typeof updateObservationSchema>>;
  observationId: string;
  children?: ReactNode;
}) => {
  const [openInternal, onOpenChangeInternal] = useState(
    openControlled ?? false,
  );
  const open = openControlled ?? openInternal;
  const onOpenChange = onOpenChangeControlled ?? onOpenChangeInternal;
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const accessToken = useAccessTokenBasic();
  const { subdomain } = useRouteContext({ from: "__root__" });
  const { siteSlug } = useParams({ from: "/_layout/site/$siteSlug" });

  const form = useForm<z.infer<typeof updateObservationSchema>>({
    defaultValues: initialValues,
    criteriaMode: "all",
    resolver: zodResolver(updateObservationSchema),
  });

  const updateObservation = useGqlMutation({
    mutation: updateObservationDocument,
    onError: () => {
      toast.error("Failed to update observation");
    },
    onSuccess: (res) => {
      if (!res.updateObservation.success) {
        toast.error("Failed to update observation");
      }
      toast.success("Observation updated");
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetDashboard],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetMaterial],
      });
      void queryClient.invalidateQueries({
        queryKey: [namedOperations.Query.GetObservation],
      });
      onOpenChange(false);
    },
  });

  const handleOpenChange = (open: boolean) => {
    if (
      Object.keys(form.formState.dirtyFields).length !== 0 &&
      !discardDialogOpen
    ) {
      setDiscardDialogOpen(true);
      return;
    }
    onOpenChange(open);
  };

  const handleSubmit = async (
    data: z.infer<typeof updateObservationSchema>,
  ) => {
    await updateObservation.mutateAsync({
      id: observationId,
      input: data,
    });
  };

  useEffect(() => {
    if (open) {
      form.reset(initialValues);
    }
    setDiscardDialogOpen(false);
  }, [open, form]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {children}
      <DialogContent
        className={twMerge(
          "top-12 flex max-h-[calc(100%-96px)] w-full max-w-[calc(100%-32px)] md:max-w-[550px] translate-y-0 flex-col gap-0! rounded-xl bg-white p-0! [@media(min-height:953px)]:top-32 [@media(min-height:953px)]:max-h-[calc(100%-256px)]",
          styles.dialogNoY,
        )}
      >
        <DialogTitle className="sr-only">Update observation</DialogTitle>
        <DialogDescription className="sr-only">
          Update observation form
        </DialogDescription>
        <FormProvider {...form}>
          <div className="absolute right-5 top-5 gap-1 flex items-center">
            <Tooltip>
              <DiscardDialog
                onDiscard={() => {
                  handleOpenChange(false);
                }}
                open={discardDialogOpen}
                onOpenChange={setDiscardDialogOpen}
              >
                <TooltipTrigger asChild>
                  <IconButton
                    ariaLabel="Close"
                    variant="tertiaryGray"
                    testId="close-edit-observation-dialog"
                    className="p-1.5"
                    size="sm"
                    icon={XClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenChange(false);
                    }}
                    onFocusCapture={stopEventPropagation}
                  />
                </TooltipTrigger>
              </DiscardDialog>
              <TooltipContent side="bottom" sideOffset={0}>
                Discard observation
              </TooltipContent>
            </Tooltip>
          </div>
          <form>
            <div className="space-y-4 px-5 pb-4 pt-5">
              <FormField
                control={form.control}
                name="condition"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ObservationConditionPicker
                        value={field.value}
                        onChange={field.onChange}
                      >
                        <Button size="xs" className="w-fit">
                          <ObservationConditionRating condition={field.value} />
                          {observationConditionLabelMap[field.value]}
                        </Button>
                      </ObservationConditionPicker>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="bodyData"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      {accessToken != null && (
                        <RichTextEditor
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Add comments..."
                          data-cy="observation-body-input"
                          extensions={[
                            mention({ accessToken, siteSlug, subdomain }),
                          ]}
                          className="rounded-none border-none shadow-none focus-within:ring-0 [&_.richTextEditor]:min-h-16 [&_.richTextEditor]:px-0 [&_.richTextEditor]:py-0"
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-grey-100 py-3 pl-4 pr-5">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="primary"
                    type="submit"
                    size="sm"
                    loading={form.formState.isSubmitting}
                    testId="submit-observation-update"
                    onFocusCapture={stopEventPropagation}
                    disabled={!open}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      form.handleSubmit(handleSubmit)(e);
                    }}
                  >
                    Update observation
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={0}>
                  Update observation
                </TooltipContent>
              </Tooltip>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
